/**
 * `/api/agents/verification-ops` — §8 scorecard M1–M12 + verification ops tiles.
 *
 * Read-only ops surface for the verification-loop program (holdout ledger,
 * admission, kill-switch drill, export.emitted audit, open-weights lock).
 *
 * ## Failure is payload, not an HTTP error
 *
 * Same contract shape as `agent-health`: the transport is always 200 and the
 * body carries `{ok:true, …} | {ok:false, error}`. A missing vault or an
 * unattached harness degrades to honest N + blocked reasons on each tile —
 * never invented numbers, never a silent omission (PLATFORM-SPEC honesty law).
 *
 * View types are mirrored here (not imported from `excellent/src`) so the
 * client route can type against the contract without dragging server-only
 * modules into the browser bundle.
 */

import { z } from "zod";
import type { Operation } from "../types";

/** One §8 scorecard cell: metric × stratum, with honest N when unmeasurable. */
export interface ScorecardMetricCellDto {
  id: string;
  name: string;
  definition: string;
  target: string;
  provenance: string;
  /** Consequence stratum bucket, or `"global"` for metrics that are not per-stratum. */
  stratum: string;
  /** Done-cohort size for this stratum (null when global / unreadable). */
  n: number | null;
  /** Numeric value when the metric produced one; null means honest N. */
  value: number | null;
  /** Human-readable note or `N — <blocked reason>`. Never fabricated. */
  display: string;
  /** True when the cell is unmeasurable (blocked registry row or n=0 / missing inputs). */
  blocked: boolean;
  blockedReason: string | null;
}

export interface HoldoutTierDto {
  tier: string;
  rows: number;
  verified: number;
  waived: number;
  unjudged: number;
}

export interface HoldoutSummaryDto {
  readable: boolean;
  blockedReason: string | null;
  rows: number | null;
  verified: number | null;
  waived: number | null;
  unjudged: number | null;
  tierCount: number | null;
  tiers: HoldoutTierDto[];
  policyVersion: number | null;
  refreshedAt: string | null;
}

export interface AdmissionStatusDto {
  readable: boolean;
  blockedReason: string | null;
  ledgerRows: number | null;
  admittedProposals: number | null;
  admittedWithinQ: number | null;
  batches: number | null;
  unparsable: number | null;
}

export interface KillSwitchDrillDto {
  readable: boolean;
  blockedReason: string | null;
  phase: string | null;
  drillId: string | null;
  start: string | null;
  end: string | null;
  reacted: boolean | null;
  workflowTitle: string | null;
  taskId: number | null;
  samples: number | null;
  signalCount: number | null;
}

export interface ExportAuditDto {
  readable: boolean;
  blockedReason: string | null;
  /** Count of `export.emitted` rows in excellent_ops_events. */
  count: number | null;
}

export interface OpenWeightsDto {
  readable: boolean;
  blockedReason: string | null;
  openInBoundary: number | null;
  closedCensused: number | null;
  grandfathered: number | null;
  frozen: number | null;
  /**
   * grandfathered / closed-censused share as a percentage when both are
   * known; null when unmeasurable (do not invent 0).
   */
  grandfatherSharePct: number | null;
  openIds: string[];
  grandfatherIds: string[];
}

export interface VerificationOpsViewDto {
  schemaVersion: 1;
  generatedAt: string;
  windowHours: number;
  /** M1–M12 cells (one per metric × stratum; globals appear once as stratum=global). */
  metrics: ScorecardMetricCellDto[];
  holdout: HoldoutSummaryDto;
  admission: AdmissionStatusDto;
  killSwitch: KillSwitchDrillDto;
  exportAudit: ExportAuditDto;
  openWeights: OpenWeightsDto;
  /** Non-fatal notes (e.g. dual-tier not attached in UI path). */
  notes: string[];
}

export type VerificationOpsResult =
  | { ok: true; status: VerificationOpsViewDto }
  | { ok: false; error: string };

const getOp: Operation<void, void, void, VerificationOpsResult> = {
  method: "GET",
  path: () => "/api/agents/verification-ops",
  response: z.custom<VerificationOpsResult>(),
};

export const verificationOps = {
  get: getOp,
};
